/**
 * store/deltaStore.js - Client-side delta storage utilities
 * Location: /app/frontend/src/admin/page_builder/store/
 * 
 * Manages style deltas at page, block, and element levels.
 * These are persisted as sparse objects - only overrides are stored.
 */

import { extractDelta, isDeltaEmpty, cleanDelta } from '../resolver/merge';

/**
 * @typedef {Object} PageStyleState
 * @property {Object} pageStyleOverrides - Page-level style overrides
 * @property {Object<string, Object>} blockStyleOverrides - Map of blockId -> style overrides
 * @property {Object<string, Object<string, Object>>} elementStyleOverrides - Map of blockId -> elementId -> style overrides
 */

/**
 * Create an empty page style state
 * @returns {PageStyleState}
 */
export function createEmptyPageStyleState() {
  return {
    pageStyleOverrides: {},
    blockStyleOverrides: {},
    elementStyleOverrides: {}
  };
}

/**
 * Get page-level style overrides
 * @param {PageStyleState} state - Page style state
 * @returns {Object} Page style overrides
 */
export function getPageOverrides(state) {
  return state?.pageStyleOverrides || {};
}

/**
 * Set page-level style overrides
 * @param {PageStyleState} state - Page style state
 * @param {Object} overrides - New page overrides
 * @returns {PageStyleState} Updated state
 */
export function setPageOverrides(state, overrides) {
  return {
    ...state,
    pageStyleOverrides: overrides
  };
}

/**
 * Get block-level style overrides
 * @param {PageStyleState} state - Page style state
 * @param {string} blockId - Block ID
 * @returns {Object} Block style overrides
 */
export function getBlockOverrides(state, blockId) {
  return state?.blockStyleOverrides?.[blockId] || {};
}

/**
 * Set block-level style overrides
 * @param {PageStyleState} state - Page style state
 * @param {string} blockId - Block ID
 * @param {Object} overrides - New block overrides
 * @returns {PageStyleState} Updated state
 */
export function setBlockOverrides(state, blockId, overrides) {
  const newBlockOverrides = { ...state.blockStyleOverrides };
  
  if (isDeltaEmpty(overrides)) {
    delete newBlockOverrides[blockId];
  } else {
    newBlockOverrides[blockId] = overrides;
  }
  
  return {
    ...state,
    blockStyleOverrides: newBlockOverrides
  };
}

/**
 * Get element-level style overrides
 * @param {PageStyleState} state - Page style state
 * @param {string} blockId - Block ID
 * @param {string} elementId - Element ID
 * @returns {Object} Element style overrides
 */
export function getElementOverrides(state, blockId, elementId) {
  return state?.elementStyleOverrides?.[blockId]?.[elementId] || {};
}

/**
 * Set element-level style overrides
 * @param {PageStyleState} state - Page style state
 * @param {string} blockId - Block ID
 * @param {string} elementId - Element ID
 * @param {Object} overrides - New element overrides
 * @returns {PageStyleState} Updated state
 */
export function setElementOverrides(state, blockId, elementId, overrides) {
  const newElementOverrides = { ...state.elementStyleOverrides };
  
  if (!newElementOverrides[blockId]) {
    newElementOverrides[blockId] = {};
  }
  
  if (isDeltaEmpty(overrides)) {
    delete newElementOverrides[blockId][elementId];
    // Clean up empty block entry
    if (Object.keys(newElementOverrides[blockId]).length === 0) {
      delete newElementOverrides[blockId];
    }
  } else {
    newElementOverrides[blockId] = {
      ...newElementOverrides[blockId],
      [elementId]: overrides
    };
  }
  
  return {
    ...state,
    elementStyleOverrides: newElementOverrides
  };
}

/**
 * Update a single style property at element level
 * @param {PageStyleState} state - Page style state
 * @param {string} blockId - Block ID
 * @param {string} elementId - Element ID
 * @param {string} property - CSS property name
 * @param {*} value - New value (null to reset)
 * @returns {PageStyleState} Updated state
 */
export function updateElementStyleProperty(state, blockId, elementId, property, value) {
  const currentOverrides = getElementOverrides(state, blockId, elementId);
  const newOverrides = { ...currentOverrides };
  
  if (value === null || value === undefined) {
    delete newOverrides[property];
  } else {
    newOverrides[property] = value;
  }
  
  return setElementOverrides(state, blockId, elementId, newOverrides);
}

/**
 * Convert page style state to API format for saving
 * @param {PageStyleState} state - Page style state
 * @returns {Object} API-ready object
 */
export function toAPIFormat(state) {
  return {
    pageStyleOverrides: cleanDelta(state.pageStyleOverrides),
    // Convert block overrides to array format if needed by API
    blockStyleOverrides: Object.entries(state.blockStyleOverrides || {}).reduce((acc, [blockId, overrides]) => {
      if (!isDeltaEmpty(overrides)) {
        acc[blockId] = cleanDelta(overrides);
      }
      return acc;
    }, {}),
    // Convert element overrides to nested format
    elementStyleOverrides: Object.entries(state.elementStyleOverrides || {}).reduce((acc, [blockId, elements]) => {
      const cleanedElements = Object.entries(elements || {}).reduce((elemAcc, [elementId, overrides]) => {
        if (!isDeltaEmpty(overrides)) {
          elemAcc[elementId] = cleanDelta(overrides);
        }
        return elemAcc;
      }, {});
      
      if (Object.keys(cleanedElements).length > 0) {
        acc[blockId] = cleanedElements;
      }
      return acc;
    }, {})
  };
}

/**
 * Load page style state from API response
 * @param {Object} apiData - API response data
 * @returns {PageStyleState} Loaded state
 */
export function fromAPIFormat(apiData) {
  return {
    pageStyleOverrides: apiData?.pageStyleOverrides || {},
    blockStyleOverrides: apiData?.blockStyleOverrides || {},
    elementStyleOverrides: apiData?.elementStyleOverrides || {}
  };
}

export default {
  createEmptyPageStyleState,
  getPageOverrides,
  setPageOverrides,
  getBlockOverrides,
  setBlockOverrides,
  getElementOverrides,
  setElementOverrides,
  updateElementStyleProperty,
  toAPIFormat,
  fromAPIFormat
};
